import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { isMobile } from 'react-device-detect';

import { CanvasContainer, ChartTotalSpan, ChartZeroResultSpan, BoldSpan, Note } from './styles';
import { categories } from 'constants/tests';

const getDistributionToUse = (test, showFullPopulation) => {
  let distribution = showFullPopulation ? test.distribution : test.companyTestDistribution;
  if (test.adaptiveTest) {
    distribution = distribution.filter((d) => d?.count);
  }
  return distribution;
};

export const DistributionChart = ({
  test,
  ticket,
  showTotalTestsTaken = true,
  showFullPopulation = true,
  forcedHeight,
}) => {
  const isFreeCode = test.category === categories.CODE.category;
  const distribution = getDistributionToUse(test, showFullPopulation);
  const differenceInterval = showFullPopulation ? test.differenceInterval : test.companyTestDifferenceInterval;
  const totalTestsTaken = distribution.reduce((tot, distEntry) => tot + (distEntry?.count ?? distEntry ?? 0), 0);
  const zeroResultCount = showFullPopulation ? (test.zeroResultCount ?? 0) : (test.zeroResultCountForCompany ?? 0);

  let columnValues = distribution.map((dist) => dist?.count ?? dist ?? 0);
  let columnColors = Array(columnValues.length).fill('#e5e5e5');
  let tooltips = {};
  let plugins = [];

  const labelsRef = useRef([]);
  const percent40Index = useRef(0);
  const percent70Index = useRef(0);

  // targetIndex used to determine column colors when chart used for ticket
  let targetIndex;

  if (ticket?._id) {
    if (test.adaptiveTest) {
      if (ticket.rating < distribution[0].maxValue) {
        targetIndex = 0;
      } else {
        const targetValue = parseInt(ticket.rating / differenceInterval) * differenceInterval;
        targetIndex = distribution.map((d) => d.maxValue).indexOf(targetValue);
      }
    } else if (test.category === categories.CODE.category) {
      const MAX_VALUE_FOR_FIRST_BAR = 4;
      const targetValue =
        0 < ticket.result && ticket.result <= MAX_VALUE_FOR_FIRST_BAR
          ? MAX_VALUE_FOR_FIRST_BAR
          : parseInt(ticket.result / differenceInterval) * differenceInterval;
      targetIndex = distribution.map((d) => d.maxValue ?? 0).indexOf(targetValue);
    } else {
      targetIndex = (ticket.result || 0) - 1;
    }

    if (targetIndex != null && targetIndex >= 0 && targetIndex < columnValues.length) {
      for (let i = 0; i < targetIndex; i += 1) {
        columnColors[i] = 'rgba(204, 233, 195, 1)';
      }
      columnColors[targetIndex] = 'rgba(126, 198, 102, 1)';
    }
  }

  if (distribution) {
    if (test.adaptiveTest) {
      let testsTakenCounter = 0;
      labelsRef.current = columnValues.map((columnValue) => {
        testsTakenCounter += columnValue;
        // Round to one decimal and convert to percentage
        return Math.round(((100 * testsTakenCounter) / totalTestsTaken) * 10) / 10 + '%';
      });
      tooltips = {
        callbacks: {
          title: (tooltipItems) => {
            return `Better than or equal to ${tooltipItems[0].label} of results`;
          },
          label: (context) => {
            const value = distribution[context.dataIndex].count;
            return `${value} test taker${value === 1 ? '' : 's'} belong to this group`;
          },
        },
      };
    } else {
      labelsRef.current = distribution.map((dist, index) => {
        return dist?.label ? dist.label : isFreeCode ? index : index + 1;
      });
      tooltips = {
        callbacks: {
          title: (context) => {
            return `Result: ${context[0].label}`;
          },
          label: (context) => {
            const value = context.dataset.data[context.dataIndex];
            return `${value} test taker${value === 1 ? '' : 's'} got this result`;
          },
        },
      };
    }
  }

  if (!isFreeCode) {
    percent40Index.current = 0;
    percent70Index.current = 0;

    let totalCount = 0;
    for (const columnIndex in columnValues) {
      let columnValue = columnValues[columnIndex];
      if (percent40Index.current === 0 && totalCount + columnValue > totalTestsTaken * 0.4)
        percent40Index.current = Number(columnIndex);
      if (percent70Index.current === 0 && totalCount + columnValue > totalTestsTaken * 0.7)
        percent70Index.current = Number(columnIndex);
      totalCount += columnValue;
    }

    plugins.push({
      id: 'annotationFor40And70Percentages',
      afterDatasetsDraw: ({ ctx, scales }) => {
        let higherIndexLabel;
        let lowerIndexLabel;
        let index;

        // 40% index bar
        if (test.adaptiveTest) {
          let label40Index = parseFloat(
            labelsRef.current[percent40Index.current].substring(0, labelsRef.current[percent40Index.current].length - 1)
          );
          if (label40Index > 40 && percent40Index.current > 0) {
            higherIndexLabel = label40Index;
            lowerIndexLabel = parseFloat(
              labelsRef.current[percent40Index.current - 1].substring(
                0,
                labelsRef.current[percent40Index.current - 1].length - 1
              )
            );
            index = percent40Index.current - 1 + (40 - lowerIndexLabel) / (higherIndexLabel - lowerIndexLabel);
          } else if (percent40Index.current + 1 < labelsRef.current.length) {
            lowerIndexLabel = label40Index;
            higherIndexLabel = parseFloat(
              labelsRef.current[percent40Index.current + 1].substring(
                0,
                labelsRef.current[percent40Index.current + 1].length - 1
              )
            );
            index = percent40Index.current + +((40 - lowerIndexLabel) / (higherIndexLabel - lowerIndexLabel));
          }
        } else {
          index = labelsRef.current[percent40Index.current];
        }

        if (index) {
          let xaxis = scales['x'];
          let yaxis = scales['y'];

          ctx.save();
          ctx.font = '15px';
          ctx.textBaseline = 'bottom';
          ctx.fillStyle = '#000000';
          ctx.fillText('40%', xaxis.getPixelForValue(index) - 30, 20);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(xaxis.getPixelForValue(index), yaxis.top);
          ctx.strokeStyle = '#C4AE1F';
          ctx.lineTo(xaxis.getPixelForValue(index), yaxis.bottom);
          ctx.stroke();
        }

        // 70% index bar
        if (test.adaptiveTest) {
          let label70Index = parseFloat(
            labelsRef.current[percent70Index.current].substring(0, labelsRef.current[percent70Index.current].length - 1)
          );
          if (label70Index > 70 && percent70Index.current > 0) {
            higherIndexLabel = label70Index;
            lowerIndexLabel = parseFloat(
              labelsRef.current[percent70Index.current - 1].substring(
                0,
                labelsRef.current[percent70Index.current - 1].length - 1
              )
            );
            index = percent70Index.current - 1 + (1 / (higherIndexLabel - lowerIndexLabel)) * (70 - lowerIndexLabel);
          } else if (percent70Index.current + 1 < labelsRef.current.length) {
            lowerIndexLabel = label70Index;
            higherIndexLabel = parseFloat(
              labelsRef.current[percent70Index.current + 1].substring(
                0,
                labelsRef.current[percent70Index.current + 1].length - 1
              )
            );
            index = percent70Index.current + (1 / (higherIndexLabel - lowerIndexLabel)) * (70 - lowerIndexLabel);
          }
        } else {
          index = labelsRef.current[percent70Index.current];
        }

        if (index) {
          let xaxis = scales['x'];
          let yaxis = scales['y'];

          ctx.save();
          ctx.font = '15px';
          ctx.textBaseline = 'bottom';
          ctx.fillStyle = '#000000';
          ctx.fillText('70%', xaxis.getPixelForValue(index) + 10, 20);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(xaxis.getPixelForValue(index), yaxis.top);
          ctx.strokeStyle = '#8EBC3C';
          ctx.lineTo(xaxis.getPixelForValue(index), yaxis.bottom);
          ctx.stroke();
        }
      },
    });
  }

  function createDiagonalPattern(color = 'black') {
    let shape = document.createElement('canvas');
    shape.width = 10;
    shape.height = 10;
    let c = shape.getContext('2d');
    c.strokeStyle = color;
    c.beginPath();
    c.moveTo(2, 0);
    c.lineTo(10, 8);
    c.stroke();
    c.beginPath();
    c.moveTo(0, 8);
    c.lineTo(2, 10);
    c.stroke();
    return c.createPattern(shape, 'repeat');
  }

  if (test.adaptiveTest) {
    const highestValue = columnValues.reduce((acc, curr) => {
      if (acc > curr) return acc;
      else return curr;
    }, 0);
    // If the first "merge" bar is half as tall as the highest bar, make it smaller and add stripes to it.
    // To make it clear it is an adjusted bar.
    if (columnValues[0] > highestValue / 2) {
      columnValues[0] = Math.floor(highestValue / 4);
      if (targetIndex !== 0) columnColors[0] = createDiagonalPattern('rgba(126, 198, 102, 1)');
    }
  }
  const chartData = {
    labels: labelsRef.current,
    datasets: [
      {
        data: columnValues,
        borderWidth: 1,
        backgroundColor: columnColors,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: tooltips,
      datalabels: false,
    },
    events: ['mousemove'],
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Results',
        },
        ticks: {
          autoSkip: false,
          // The 90 & 0 are the default values
          maxRotation: isMobile ? 90 : isFreeCode ? 45 : 90,
          minRotation: isMobile ? 90 : isFreeCode ? 45 : 0,
          font: { size: isMobile ? 8 : 12 },
        },
        grid: {
          display: false,
        },
        stacked: true,
      },

      y: {
        title: {
          scaleStyle: 'bold',
          display: true,
          text: 'Tests Taken',
        },
        grid: {
          display: false,
        },
        beginAtZero: true,
        ticks: {
          font: { size: isMobile ? 8 : 12 },
        },
      },
    },
  };

  const renderMissingDataPlaceholder = () => (
    <CanvasContainer height="100px">
      <Note>
        We are currently collecting data about this test and you will soon be able to see a test distribution.
      </Note>
    </CanvasContainer>
  );

  const renderBarChart = () => (
    <CanvasContainer height={forcedHeight}>
      {showTotalTestsTaken && (
        <>
          <ChartTotalSpan>
            Total tests taken:
            <BoldSpan>{totalTestsTaken + zeroResultCount}</BoldSpan>
          </ChartTotalSpan>
          {zeroResultCount > 0 && (
            <ChartZeroResultSpan>out of which {zeroResultCount} got 0 points</ChartZeroResultSpan>
          )}
        </>
      )}
      <Bar data={chartData} options={options} plugins={plugins}></Bar>
    </CanvasContainer>
  );

  return totalTestsTaken < 15 && showFullPopulation ? renderMissingDataPlaceholder() : renderBarChart();
};
